export type ChallengeCalculationTrade = {
  id: number;
  date: string;
  symbol: string;
  pnl: number;
};

export function calculateAccountGrowth(currentBalance: number, initialInvestment: number): number {
  if (initialInvestment <= 0) return 0;
  return ((currentBalance - initialInvestment) / initialInvestment) * 100;
}

export function calculateContribution(
  totalVideoLikes: number,
  contributionPerLike: number,
  maximumContribution: number,
): number {
  if (
    !Number.isFinite(totalVideoLikes)
    || !Number.isFinite(contributionPerLike)
    || !Number.isFinite(maximumContribution)
    || totalVideoLikes < 0
    || contributionPerLike < 0
    || maximumContribution < 0
  ) {
    return 0;
  }

  return Math.min(totalVideoLikes * contributionPerLike, maximumContribution);
}

export function calculateChallengePreview(
  currentBalance: number,
  startingCapital: number,
  challengeCapitalGoal: number,
) {
  const current = Number.isFinite(currentBalance) ? currentBalance : 0;
  const starting = Number.isFinite(startingCapital) ? startingCapital : 0;
  const goal = Number.isFinite(challengeCapitalGoal) ? challengeCapitalGoal : 0;

  return {
    currentBalance: current,
    totalGain: current - starting,
    accountGrowthPercent: starting > 0 ? ((current - starting) / starting) * 100 : null,
    currentBalanceVsGoalPercent: goal > 0 ? (current / goal) * 100 : null,
    remainingToGoal: goal - current,
    startingBalanceMultiple: starting > 0 ? current / starting : null,
  };
}

export function calculateChallengeMetrics<T extends ChallengeCalculationTrade>(
  startingBalance: number,
  targetGoal: number,
  trades: T[],
  startDateLabel = 'Start',
) {
  const starting = Number.isFinite(startingBalance) ? startingBalance : 0;
  const target = Number.isFinite(targetGoal) ? targetGoal : 0;
  const sortedTrades = [...trades].sort((a, b) => {
    const left = Date.parse(a.date);
    const right = Date.parse(b.date);
    return (Number.isFinite(left) ? left : 0) - (Number.isFinite(right) ? right : 0);
  });

  let cumulativePnl = 0;
  let winningTrades = 0;
  let totalWinAmount = 0;
  let losingTrades = 0;
  let totalLossAmount = 0;
  let maxSingleTradeDrawdown = 0;
  const equityTrajectory = [{ name: startDateLabel, balance: starting }];
  const chronologySeries: Array<{ name: string; pnl: number; tradeData: T }> = [];

  sortedTrades.forEach((trade, index) => {
    const pnl = Number.isFinite(Number(trade.pnl)) ? Number(trade.pnl) : 0;
    cumulativePnl += pnl;
    equityTrajectory.push({ name: trade.date, balance: starting + cumulativePnl });
    chronologySeries.push({ name: `#${index + 1} (${trade.symbol})`, pnl, tradeData: trade });
    if (pnl > 0) {
      winningTrades += 1;
      totalWinAmount += pnl;
    } else if (pnl < 0) {
      losingTrades += 1;
      totalLossAmount += pnl;
    }
    maxSingleTradeDrawdown = Math.min(maxSingleTradeDrawdown, pnl);
  });

  const currentBalance = starting + cumulativePnl;
  const remainingBalance = Math.max(0, target - currentBalance);
  const accountGrowthPct = calculateAccountGrowth(currentBalance, starting);
  const totalTrades = sortedTrades.length;
  const progressPct = target > 0
    ? Math.min(100, Math.max(0, Number(((currentBalance / target) * 100).toFixed(1))))
    : 0;
  const tradingDays = new Set(sortedTrades.map((trade) => trade.date.trim()).filter(Boolean)).size;
  const averageWin = winningTrades > 0 ? totalWinAmount / winningTrades : 0;
  const averageLoss = losingTrades > 0 ? totalLossAmount / losingTrades : 0;

  return {
    currentBalance,
    remainingBalance,
    cumulativePnl,
    totalTrades,
    winRate: totalTrades > 0 ? Math.round((winningTrades / totalTrades) * 100) : 0,
    progressPct,
    remainingPct: Number((100 - progressPct).toFixed(1)),
    accountGrowthPct,
    growthMultiplier: starting > 0 ? currentBalance / starting : 0,
    averageWin,
    averageLoss,
    averageWinLossRatio: averageLoss < 0 ? averageWin / Math.abs(averageLoss) : 0,
    maxDrawdown: maxSingleTradeDrawdown,
    tradingDays,
    averageTradesPerDay: tradingDays > 0 ? totalTrades / tradingDays : 0,
    equityTrajectory,
    chronologySeries,
    sortedTrades,
  };
}
